"use client";

import { ArrowDown, ArrowUp, NotebookPen, Pencil, Trash2 } from "lucide-react";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { InTextCitationPopover } from "@/components/in-text-citation-popover";
import { Button } from "@/components/ui/button";
import type { SavedCitation } from "@/lib/bibliography";
import { t } from "@/lib/i18n/interpolate";
import { cn } from "@/lib/utils";

interface ReferenceRowProps {
  item: SavedCitation;
  index: number;
  styleName: string;
  checked: boolean;
  /** Open in the inspector. */
  active: boolean;
  /** Manual ordering exposes move up/down. */
  manual: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (direction: "up" | "down") => void;
}

/**
 * One reference in the workspace list. The formatted entry is the row — no card
 * chrome around it — so the list reads like the finished reference page. Actions are
 * always visible (the mobile e2e suite asserts this) but muted until hover.
 */
export function ReferenceRow({
  item,
  index,
  styleName,
  checked,
  active,
  manual,
  isFirst,
  isLast,
  onToggle,
  onOpen,
  onEdit,
  onDelete,
  onMove,
}: ReferenceRowProps) {
  const bib = useBibStrings();
  const title = item.entry.title || item.citation;
  return (
    <li
      className={cn(
        "group relative grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 px-3 py-3 transition-colors sm:grid-cols-[auto_auto_minmax(0,1fr)_auto] sm:gap-x-4 sm:px-4",
        active ? "bg-primary/[0.06] dark:bg-primary/10" : "hover:bg-muted/30",
        checked && !active ? "bg-muted/40" : ""
      )}
    >
      <input
        aria-label={t(bib.selectCitation, { title })}
        checked={checked}
        className="mt-1 size-4 shrink-0 accent-primary"
        onChange={onToggle}
        type="checkbox"
      />
      <span className="hidden w-6 pt-0.5 text-end text-muted-foreground text-xs tabular-nums sm:block">
        {index + 1}
      </span>
      <div className="min-w-0">
        <button
          className="block w-full text-start"
          onClick={onOpen}
          type="button"
        >
          <span className="wrap-break-word block whitespace-pre-wrap font-serif text-[15px] text-foreground leading-relaxed">
            {item.citation}
          </span>
        </button>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-xs">
          <span className="rounded-full bg-secondary px-2 py-0.5">
            {styleName}
          </span>
          {item.note ? (
            <span
              className="inline-flex items-center gap-1"
              title={bib.noteLabel}
            >
              <NotebookPen className="size-3" />
              <span className="sr-only">{bib.noteLabel}</span>
            </span>
          ) : null}
        </div>
      </div>
      <div className="col-start-2 flex flex-wrap items-center gap-1 sm:col-start-4 sm:row-start-1 sm:flex-nowrap sm:opacity-70 sm:group-hover:opacity-100 sm:focus-within:opacity-100">
        {manual ? (
          <>
            <Button
              disabled={isFirst}
              onClick={() => onMove("up")}
              size="icon-xs"
              title={bib.moveUp}
              variant="ghost"
            >
              <ArrowUp className="size-3.5" />
              <span className="sr-only">{bib.moveUp}</span>
            </Button>
            <Button
              disabled={isLast}
              onClick={() => onMove("down")}
              size="icon-xs"
              title={bib.moveDown}
              variant="ghost"
            >
              <ArrowDown className="size-3.5" />
              <span className="sr-only">{bib.moveDown}</span>
            </Button>
          </>
        ) : null}
        <InTextCitationPopover citation={item} styleName={styleName} />
        <Button
          onClick={onEdit}
          size="icon-xs"
          title={bib.editTitle}
          variant="ghost"
        >
          <Pencil className="size-3.5" />
          <span className="sr-only">{bib.editTitle}</span>
        </Button>
        <Button
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onDelete}
          size="icon-xs"
          title={bib.deleteTitle}
          variant="ghost"
        >
          <Trash2 className="size-3.5" />
          <span className="sr-only">{bib.deleteTitle}</span>
        </Button>
      </div>
    </li>
  );
}
